import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.js';
import { useTenant } from '../context/TenantContext.js';
import { LoadingSpinner } from './LoadingSpinner.js';
import { ErrorMessage } from './ErrorMessage.js';

export function ProtectedRoute({
  children,
  requireAdmin = false,
  skipServerCheck = false,
}: {
  children: React.ReactNode;
  requireAdmin?: boolean;
  skipServerCheck?: boolean;
}) {
  const { user, loading, needsTotpValidation } = useAuth();
  const { hasServer, loading: tenantLoading, error: tenantError, refreshTenants } = useTenant();

  if (loading) {
    return (
      <div className="page-loading">
        <LoadingSpinner text="Checking session..." />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // platform admin with 2FA not set up → force setup
  if (user.role === 'platform_admin' && !user.totpEnabled) {
    return <Navigate to="/totp-setup" replace />;
  }

  // user has 2FA → must validate before proceeding
  if (needsTotpValidation) {
    return <Navigate to="/totp-verify" replace />;
  }

  // admin-only route check
  if (requireAdmin && user.role !== 'platform_admin') {
    return <Navigate to="/" replace />;
  }

  // skip whitelist/server checks for onboarding and pending-approval pages
  if (!skipServerCheck) {
    // non-whitelisted non-admin → pending approval
    if (user.role !== 'platform_admin' && !user.whitelisted) {
      return <Navigate to="/pending-approval" replace />;
    }

    // still loading tenant data
    if (tenantLoading) {
      return (
        <div className="page-loading">
          <LoadingSpinner text="Loading your server..." />
        </div>
      );
    }

    // tenant fetch failed — show error with retry, NOT onboarding redirect
    if (tenantError && !hasServer) {
      return (
        <div className="page-loading">
          <ErrorMessage message={tenantError} onRetry={() => void refreshTenants()} />
        </div>
      );
    }

    // whitelisted user with no server → onboarding wizard
    if (!hasServer && user.role !== 'platform_admin') {
      return <Navigate to="/onboarding" replace />;
    }
  }

  return <>{children}</>;
}
